<script setup lang="ts">
import DetailItem from '~/components/common/DetailItem.vue'
import PlatformLinks from '~/components/common/PlatformLinks.vue'
import type { Company } from '~~/server/types/company'

interface Props {
  company: Partial<Company>
}
const props = defineProps<Props>()
const formatRevenue = (revenues: { [key: string]: number } | undefined) => {
  if (!revenues) return 'Revenue not available'
  const entries = Object.entries(revenues).sort(
    ([yearA], [yearB]) => Number(yearB) - Number(yearA)
  )
  if (entries.length === 0) return 'Revenue not available'
  const [latestYear, value] = entries[0]
  let formattedValue
  if (value >= 1e12) {
    formattedValue = `$${(value / 1e12).toFixed(0)}T`
  } else if (value >= 1e9) {
    formattedValue = `$${(value / 1e9).toFixed(0)}B`
  } else if (value >= 1e6) {
    formattedValue = `$${(value / 1e6).toFixed(0)}M`
  } else {
    formattedValue = `$${value.toFixed(0)}`
  }
  return `${formattedValue} in ${latestYear}`
}
const details = computed(() => [
  {
    label: 'Website',
    value: props.company?.links?.['website'] || 'Website not available',
    isLink: Boolean(props.company?.links?.['website']),
    showLabel: true
  },
  {
    label: 'Company Size',
    value: props.company?.size
      ? `${props.company.size.toLocaleString()}+ employees`
      : 'Size not available',
    showLabel: true
  },
  {
    label: 'Founded in',
    value: props.company?.year_founded?.toString() || 'N/A',
    showLabel: true
  },
  {
    label: 'Location',
    value: props.company?.headquarters_location || 'Location not available',
    showLabel: true
  },
  {
    label: 'Industry',
    value: props.company?.industry || 'N/A',
    showLabel: true
  },
  {
    label: 'Revenue',
    value: formatRevenue(
      props.company?.revenue_usd_per_year as { [key: string]: number }
    ),
    showLabel: true
  },
  { label: 'CEO', value: props.company?.ceo_name || 'N/A', showLabel: true }
])
const hasPlatformLinks = computed(() => {
  if (!props.company?.links) return false
  const supportedPlatforms = [
    'linkedin',
    'facebook',
    'instagram',
    'twitter',
    'x',
    'youtube',
    'tiktok',
    'pinterest',
    'github',
    'snapchat',
    'wikipedia',
    'careers'
  ]
  return supportedPlatforms.some(
    platform => props.company?.links?.[platform]
  )
})
</script>

<template>
  <Card>
    <div class="flex flex-col gap-6 p-6">
      <div class="w-full">
        <DetailItem
          v-bind="details[0]"
          class="w-full"
        />
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <DetailItem
          v-for="detail in details.slice(1)"
          :key="detail.label"
          v-bind="detail"
          class="w-full"
        />
      </div>
      <div
        v-if="hasPlatformLinks"
        class="mt-4"
      >
        <PlatformLinks :links="company?.links || {}" />
      </div>
    </div>
  </Card>
</template>
